import { z } from 'zod';

import { baseEntitySchema } from './baseContract';

// Optional email field that also accepts an empty string — a plain
// `z.string().email().nullable().optional()` rejects `''`, which a form
// field settles on the instant a user clears it after dirtying it.
const optionalEmailSchema = z
  .string()
  .email('Invalid email format')
  .or(z.literal(''))
  .nullable()
  .optional();

// Main schema for OrganizationBillingInfo, closely matching the entity
export const organizationBillingInfoSchema = baseEntitySchema.extend({
  organizationId: z.string().uuid('Invalid Organization ID'),
  isCompany: z.boolean().default(false),
  companyName: z.string().nullable().optional(),
  registrationNumber: z.string().nullable().optional(), // e.g., IČO
  vatId: z.string().nullable().optional(),
  addressLine1: z.string().nullable().optional(),
  addressLine2: z.string().nullable().optional(),
  city: z.string().nullable().optional(),
  postalCode: z.string().nullable().optional(),
  country: z.string().nullable().optional(), // Consider a specific enum/list later
  billingEmail: optionalEmailSchema,
  invoiceCcEmail: optionalEmailSchema,
});

export type OrganizationBillingInfoDTO = z.infer<
  typeof organizationBillingInfoSchema
>;

// Schema for creating new OrganizationBillingInfo
export const createOrganizationBillingInfoInputSchema = z
  .object({
    organizationId: z.string().uuid('Organization ID is required'),
    isCompany: z.boolean().optional().default(false),
    companyName: z
      .string()
      .min(1, 'Company name is required if isCompany is true')
      .nullable()
      .optional(),
    registrationNumber: z.string().nullable().optional(),
    vatId: z.string().nullable().optional(),
    addressLine1: z
      .string()
      .min(1, 'Address line 1 is required')
      .nullable()
      .optional(),
    addressLine2: z.string().nullable().optional(),
    city: z.string().min(1, 'City is required').nullable().optional(),
    postalCode: z
      .string()
      .min(1, 'Postal code is required')
      .nullable()
      .optional(),
    country: z.string().min(1, 'Country is required').nullable().optional(),
    billingEmail: optionalEmailSchema,
    invoiceCcEmail: optionalEmailSchema,
  })
  .refine(
    data => {
      // If it's a company, companyName must be provided.
      return !(data.isCompany && !data.companyName);
    },
    {
      message: 'Company name is required when billing to a company.',
      path: ['companyName'],
    },
  );

export type CreateOrganizationBillingInfoInputDTO = z.infer<
  typeof createOrganizationBillingInfoInputSchema
>;

// Schema for updating existing OrganizationBillingInfo
export const updateOrganizationBillingInfoInputSchema = z.object({
  organizationId: z.string().uuid('Organization ID is required'),
  isCompany: z.boolean().optional(),
  companyName: z.string().nullable().optional(),
  registrationNumber: z.string().nullable().optional(),
  vatId: z.string().nullable().optional(),
  addressLine1: z.string().nullable().optional(),
  addressLine2: z.string().nullable().optional(),
  city: z.string().nullable().optional(),
  postalCode: z.string().nullable().optional(),
  country: z.string().nullable().optional(),
  billingEmail: optionalEmailSchema,
  invoiceCcEmail: optionalEmailSchema,
});

export type UpdateOrganizationBillingInfoInputDTO = z.infer<
  typeof updateOrganizationBillingInfoInputSchema
>;

// Schema for ARES company lookup response
export const aresCompanyInfoSchema = z.object({
  ico: z.string(),
  dic: z.string().optional(), // VAT ID, often prefixed with CZ
  obchodniJmeno: z.string(), // Company Name
  sidlo: z.object({
    textovaAdresa: z.string().optional(), // Full address as text
    ulice: z.string().optional(), // Street
    cisloDomovni: z.string().optional(), // House number
    cisloOrientacni: z.string().optional(), // Orientation number (if any)
    obec: z.string().optional(), // Municipality / City
    psc: z.string().optional(), // Postal code
    stat: z.string().optional(), // Country code (e.g., CZ)
  }),
  pravniForma: z.string().optional(), // Legal form
  // Add other fields as needed from the ARES API response
});

export type AresCompanyInfoDTO = z.infer<typeof aresCompanyInfoSchema>;

// Schema for the mapped ARES company lookup response, fitting our billing structure
export const aresLookupResponseSchema = z.object({
  isCompany: z.literal(true).default(true), // It's always a company from ARES
  companyName: z.string().optional(),
  registrationNumber: z.string().optional(), // ICO
  vatId: z.string().optional(), // DIC
  addressLine1: z.string().optional(), // Will hold textovaAdresa for simplicity
  addressLine2: z.string().optional(), // Will hold obec for simplicity
  city: z.string().optional(), // Will hold obec for simplicity
  postalCode: z.string().optional(), // Will hold psc for simplicity
  // city, postalCode, addressLine2 are harder to parse reliably from textovaAdresa
  country: z.string().optional().default('CZ'), // Default to Czech Republic (ISO code)
});
export type AresLookupResponseDTO = z.infer<typeof aresLookupResponseSchema>;
